import { useUserEventLogger } from '@repo/tracking-service';
import { Button } from '@zenterprise-inc/ui';
import cn from 'clsx';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { LocationInput } from '@/__generated__/businessLocationsMutation.graphql';
import { YearInputDrawer } from '@/components/survey/business/first-location/LocationMoveYearInputDrawer';
import { SurveyTopNavigation } from '@/components/survey/common/TopNavigation';
import { useSurveyUserEventAttributes } from '@/components/survey/common/hooks/use-survey-user-event-attributes';
import { ProgressBar } from '@/components/survey/common/ui';
import common from '@/components/survey/common/ui/styles/common.module.scss';
import { upsertLocations } from '@/graphql/mutation/survey/business';
import { SURVEY_PAGES } from '@/lib/constants/paths';
import { useUpsertBusinessAnswer } from '@/lib/hooks/api/mutation/survey/use-upsert-business-answer';
import {
  locationsDataAtom,
  readonlyHometaxAddressIndexAtom,
  readonlyHometaxAddressLocationAtom,
  readonlyLocationsForServerAtom
} from '@/lib/stores/survey/business';

export const LocationMoveYearContainer = () => {
  const router = useRouter();
  const { sendClickEvent } = useUserEventLogger();
  const eventAttributes = useSurveyUserEventAttributes();
  const [upsertAnswer, isLoading] = useUpsertBusinessAnswer(upsertLocations);

  const locations = useAtomValue(locationsDataAtom);
  const hometaxAddressLocation = useAtomValue(readonlyHometaxAddressLocationAtom);
  const hometaxAddressIdx = useAtomValue(readonlyHometaxAddressIndexAtom);
  const locationsForServerSchema = useAtomValue(readonlyLocationsForServerAtom);

  const [selectedYear, setSelectedYear] = useState(0);

  useEffect(() => {
    if (hometaxAddressLocation.year) {
      setSelectedYear(hometaxAddressLocation.year);
    }
  }, [hometaxAddressLocation, setSelectedYear]);

  const handleSubmit = (payload: LocationInput[]) => {
    upsertAnswer(
      { isFirstLocation: locations.isFirstLocation, locations: payload },
      {
        onCompleted: () => {
          router.push(SURVEY_PAGES.주소이전_홈);
        }
      }
    );
  };

  return (
    <>
      <SurveyTopNavigation leftClickEvent={'survey_relocation-address_back'} />
      <main className={cn(common.main_layout, 'pb-[162px]')}>
        <ProgressBar step={5} />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendClickEvent('survey_relocation-address_confirm', eventAttributes);

            const payload = locationsForServerSchema.slice();
            payload.splice(
              hometaxAddressIdx,
              1,
              Object.assign({}, locationsForServerSchema[hometaxAddressIdx], {
                year: selectedYear
              })
            );
            handleSubmit(payload);
          }}
        >
          <article>
            <h1 className={common.content_title}>‘{locations.hometaxAddress}’으로 언제 이전하셨나요?</h1>

            <YearInputDrawer
              label={'이전한 연도'}
              selectedYear={selectedYear}
              onSelectYear={(year) => {
                sendClickEvent('survey_relocation-address_select-year');

                setSelectedYear(year);
              }}
              openingYear={parseInt(locations.openingDate.slice(0, 4))}
            />
          </article>

          <Button.StickyBottom.Wrapper>
            <div className={'px-4.5 flex w-full flex-col items-center gap-3'}>
              <Button.StickyBottom.Button type={'submit'} state={'primary'} disabled={!selectedYear || isLoading}>
                {selectedYear ? `${selectedYear}년도 이전함` : '이전한 연도를 입력해주세요'}
              </Button.StickyBottom.Button>
              <button
                type={'button'}
                className={'text-15 inline-flex py-2.5 text-gray-700'}
                onClick={() => {
                  sendClickEvent('survey_relocation-address_confirm-unknown', eventAttributes);

                  const payload = locationsForServerSchema.slice();
                  payload.splice(
                    hometaxAddressIdx,
                    1,
                    Object.assign({}, locationsForServerSchema[hometaxAddressIdx], {
                      year: null,
                      isUnknownRelocationYear: true
                    })
                  );
                  handleSubmit(payload);
                }}
              >
                {'이전 연도를 잘 모르겠어요$>;'}
              </button>
            </div>
          </Button.StickyBottom.Wrapper>
        </form>
      </main>
    </>
  );
};
